<!--
  REV-21367 — closing document hub (Pure View).

  Selector contract: tests bind to `data-testid`, `aria-sort`, `aria-current` and `data-*` markers,
  never to styling classes and never to translated prose. See e2e-lab/documents-hub.lab.spec.ts.

  Three places where ADR-0009 (accepted 11.08.2026) overrules the live prototype:
    · NO trash icon anywhere — only the eye (§4)
    · THREE filter pills, all always visible; the prototype's `Nicht zuweisbar` does not exist (§9a)
    · the drop copy says PDF only, not "PDF, JPG, PNG" (§12)
-->
<div
    class="dh"
    data-testid="documents-hub"
    [attr.data-dragover]="isDragOver"
    (dragover)="onDragOver($event)"
    (dragleave)="onDragLeave()"
    (drop)="onDrop($event)"
>
    <!-- ABLEGEFLÄCHE — der Hub nimmt die Dateien an und gibt sie weiter; hochladen tut das
         Modal aus REV-21363, von REV-21376 geöffnet. Deshalb hier kein Fortschritt (§11). -->
    <div class="dh-drop" data-testid="documents-drop-overlay" *ngIf="isDragOver">
        <div class="dh-drop__card">
            <lucide-icon class="dh-drop__icon" name="upload" [size]="40" aria-hidden="true"></lucide-icon>
            <div class="dh-drop__title">{{ 'abcp_documents_drop_title' | i18next }}</div>
            <div class="dh-drop__hint">{{ 'abcp_documents_drop_hint' | i18next }}</div>
        </div>
    </div>

    <div class="dh-toolbar">
        <!-- DIE DREI PILLEN — immer alle drei, auch bei Zähler 0: nur so ist sichtbar, dass sie
             sich auf die Gesamtzahl summieren (§9a). -->
        <div class="dh-pills">
            <ham-filter-pill
                data-testid="documents-filter-pill"
                *ngFor="let key of filterKeys"
                [attr.data-filter]="key"
                [attr.aria-current]="key === activeFilter ? 'true' : null"
                [label]="filterLabelKey(key) | i18next"
                [count]="filterCount(key)"
                [active]="key === activeFilter"
                (pillClick)="onFilterClick(key)"
            ></ham-filter-pill>
        </div>

        <!-- N3 (#84) — „automatisch zugewiesen" als Facette QUER zu den drei Pillen (F7). KEINE
             vierte Pille: eigener `data-testid`, eigenes Element, damit `documents-filter-pill`
             eine echte Partition bleibt (`documents-hub.lab.spec.ts` nagelt `toHaveCount(3)` fest). -->
        <ham-filter-pill
            data-testid="documents-agent-filter"
            icon="bot-message-square"
            [label]="'abcp_documents_agent_filter' | i18next"
            [count]="agentCount"
            [active]="agentOnly"
            (pillClick)="onAgentFilterToggle()"
        ></ham-filter-pill>

        <div class="dh-spacer"></div>

        <!-- KEIN Suchfeld: es lebt in der Reiterleiste der übergeordneten Seite („Alle Dokumente
             durchsuchen…"), nicht im Hub. `searchTerm`/`searchChange` bleiben als Naht bestehen,
             damit der Container filtern kann, ohne dass sich die Schnittstelle je ändert. -->
        <!-- SYNC — Einstieg hier, Modal in REV-21376 (§15). `Verbindung trennen` fehlt bewusst:
             eine Verbindungsmutation gehört in den REV-21057-Container. -->
        <div class="dh-sync">
            <button
                class="dh-sync__main"
                type="button"
                data-testid="documents-sync-button"
                [attr.data-pending]="syncPending"
                [attr.aria-busy]="syncPending"
                [attr.data-blocked]="syncBlocked"
                [hamTooltip]="syncBlocked ? ('abcp_sync_refused_closing_sealed' | i18next) : ''"
                tooltipClass="abcp"
                [disabled]="syncPending || syncBlocked"
                (click)="onSyncNowClick()"
            >
                <lucide-icon
                    class="dh-sync__icon"
                    [class.is-spinning]="syncPending"
                    name="refresh-cw"
                    [size]="13"
                    aria-hidden="true"
                ></lucide-icon>
                <span>{{ 'abcp_documents_source_sharepoint' | i18next }}</span>
                <span class="dh-sync__tenant" *ngIf="syncConnected && syncTenant">· {{ syncTenant }}</span>
                <span class="dh-sync__tenant" *ngIf="!syncConnected">· {{ 'abcp_documents_sync_disconnected' | i18next }}</span>
            </button>
            <button
                class="dh-sync__caret"
                type="button"
                data-testid="documents-sync-caret"
                [attr.aria-expanded]="isSyncMenuOpen"
                [attr.aria-label]="'abcp_documents_sync_settings' | i18next"
                (click)="onSyncMenuToggle(); $event.stopPropagation()"
            >
                <lucide-icon name="chevron-down" [size]="13" aria-hidden="true"></lucide-icon>
            </button>

            <div
                class="dh-sync__menu"
                data-testid="documents-sync-menu"
                *ngIf="isSyncMenuOpen"
                (click)="$event.stopPropagation()"
            >
                <button
                    class="dh-sync__item"
                    type="button"
                    data-testid="documents-sync-now"
                    [attr.data-pending]="syncPending"
                    [attr.data-blocked]="syncBlocked"
                    [hamTooltip]="syncBlocked ? ('abcp_sync_refused_closing_sealed' | i18next) : ''"
                    tooltipClass="abcp"
                    [disabled]="syncPending || syncBlocked"
                    (click)="onSyncNowClick()"
                >
                    <lucide-icon
                        class="dh-sync__icon"
                        [class.is-spinning]="syncPending"
                        name="refresh-cw"
                        [size]="14"
                        aria-hidden="true"
                    ></lucide-icon>
                    {{ 'abcp_documents_sync_now' | i18next }}
                </button>
                <button
                    class="dh-sync__item"
                    type="button"
                    data-testid="documents-sync-settings"
                    (click)="onSyncSettingsClick()"
                >
                    <lucide-icon name="settings" [size]="14" aria-hidden="true"></lucide-icon>
                    {{ 'abcp_documents_sync_settings' | i18next }}
                </button>
            </div>
        </div>

        <ham-button
            data-testid="documents-upload-button"
            variant="primary"
            size="toolbar"
            icon="assets/hamIcons/upload.svg"
            (buttonClick)="onUploadClick()"
        >
            {{ 'abcp_documents_upload' | i18next }}
        </ham-button>
    </div>

    <!-- REV-21964 · R3/R6/U1 — ABWEISUNG und LAUFBERICHT, beide ueber der Tabelle.
         Hier und nicht im Menue: sie antworten auf einen Klick, den der Nutzer gerade getan hat,
         und ein Menue, das sich beim Klick schliesst, waere der schlechteste Ort dafuer. -->

    <!-- Eine Abweisung ist eine ANTWORT, kein Fehler (R3). Deshalb `role="status"` und nicht
         `alert`: „Es laeuft bereits" ist eine Auskunft, und ein Fehlerbanner wuerde genau zu der
         einen Handlung einladen, die in allen drei Faellen falsch ist — noch einmal druecken. -->
    <div class="dh-sync-note" data-testid="documents-sync-refusal" role="status" *ngIf="syncRefusal">
        <lucide-icon class="dh-sync-note__icon" name="alert-circle" [size]="15" aria-hidden="true"></lucide-icon>
        <span>{{ refusalLabelKeys[syncRefusal] | i18next }}</span>
    </div>

    <div class="dh-sync-report" data-testid="documents-sync-report" *ngIf="syncReport as report">
        <div class="dh-sync-report__head">
            <span class="dh-sync-report__status" [attr.data-status]="report.status">
                {{ runStatusLabelKeys[report.status] | i18next }}
            </span>
            <span class="dh-sync-report__time">{{ report.finishedAt ?? report.startedAt | date: dateFormat }}</span>

            <!-- One number for what came in. Orange only for a real problem; files already in the
                 closing are not one and stay in the list below. -->
            <span class="dh-sync-report__count" data-testid="documents-sync-count-created">
                {{
                    report.created > 0
                        ? ('abcp_sync_new_documents' | i18next: { count: report.created })
                        : ('abcp_sync_new_documents_none' | i18next)
                }}
            </span>
            <span
                class="dh-sync-report__count dh-sync-report__count--warn"
                data-testid="documents-sync-count-problem-files"
                *ngIf="syncProblemFiles > 0"
            >
                {{ 'abcp_sync_problem_files' | i18next: { count: syncProblemFiles } }}
            </span>
            <span
                class="dh-sync-report__count dh-sync-report__count--warn"
                data-testid="documents-sync-count-failed-folders"
                *ngIf="report.failedFolders > 0"
            >
                {{ 'abcp_sync_failed_folders' | i18next: { count: report.failedFolders } }}
            </span>

            <!-- Nur die LISTE klappt. Sie kann lang werden, die Zahlen darueber nie. -->
            <ham-button
                class="dh-sync-report__toggle"
                data-testid="documents-sync-report-toggle"
                *ngIf="report.skips.length > 0 || report.failures.length > 0"
                variant="transparent"
                size="toolbar"
                icon="assets/hamIcons/chevron-down.svg"
                [class.is-open]="isSyncReportOpen"
                [hideText]="true"
                (buttonClick)="onSyncReportToggle()"
            ></ham-button>
        </div>

        <div class="dh-sync-report__body" *ngIf="isSyncReportOpen">
            <!-- R1 — ein nicht lesbarer ORDNER ist kein uebersprungenes File: uebersprungen heisst
                 „wir haben hingesehen und entschieden", nicht lesbar heisst „wir sind nie
                 hingekommen" — und dahinter steht eine unbekannte Zahl Dokumente. Deshalb oben. -->
            <ul class="dh-sync-report__list" *ngIf="report.failures.length > 0">
                <li class="dh-sync-report__row dh-sync-report__row--fail" *ngFor="let failure of report.failures">
                    <lucide-icon name="unlink" [size]="13" aria-hidden="true"></lucide-icon>
                    <span>{{ 'abcp_sync_folder_failed' | i18next: { folderPath: failure.folderPath } }}</span>
                    <span class="dh-sync-report__detail" *ngIf="failure.message">{{ failure.message }}</span>
                </li>
            </ul>

            <ul class="dh-sync-report__list" *ngIf="report.skips.length > 0">
                <li class="dh-sync-report__row" *ngFor="let skip of report.skips">
                    <span class="dh-sync-report__file">{{ skip.filename }}</span>
                    <span class="dh-sync-report__reason">{{ skipReasonLabelKeys[skip.reason] | i18next }}</span>
                </li>
            </ul>
        </div>

        <!-- MUSS sichtbar sein, und zwar ausserhalb der Klappe: die Zahl oben bleibt exakt, waehrend
             die Liste aufhoert. Eine Liste, die einfach endet, sagt dem Treuhaender, alles Uebrige
             sei gutgegangen — genau die stille Kuerzung, die ADR-0009 §12 verbietet. -->
        <p class="dh-sync-report__truncated" data-testid="documents-sync-truncated" *ngIf="report.skipsTruncated">
            {{ 'abcp_sync_skips_truncated' | i18next }}
        </p>
    </div>

    <div class="dh-scroll">
        <table class="dh-table" data-testid="documents-table">
            <thead>
                <tr class="dh-head">
                    <!-- Alle sechs Spalten sind sortierbar; die Sortierung selbst macht der
                         Container. Der Sortierkopf füllt die Zelle, damit ein Klick auf die Spalte
                         ihn trifft. -->
                    <th
                        class="dh-head__cell"
                        data-testid="documents-column"
                        scope="col"
                        *ngFor="let key of columns"
                        [attr.data-sort-key]="key"
                        [attr.aria-sort]="ariaSort(key)"
                        [hamTooltip]="columnHintKey(key) ? (columnHintKey(key) | i18next) : ''"
                        tooltipClass="abcp"
                        [ngClass]="'dh-head__cell--' + key"
                    >
                        <abcp-sort-header
                            [direction]="sortDirection(key)"
                            [align]="key === 'confidence' ? 'center' : 'start'"
                            (sortClick)="onSortClick(key)"
                        >
                            {{ columnLabelKey(key) | i18next }}
                        </abcp-sort-header>
                    </th>
                    <th class="dh-head__cell dh-head__cell--actions" scope="col"></th>
                </tr>
            </thead>

            <tbody>
                <!-- Zustände, Präzedenz loading > error > empty > filled (Muster: accounts-overview). -->
                <tr *ngIf="showSkeleton">
                    <td class="dh-state" [attr.colspan]="columnCount">
                        <div class="dh-loading" data-testid="documents-loading">
                            <div class="dh-skeleton" *ngFor="let row of skeletonRows" aria-hidden="true"></div>
                        </div>
                    </td>
                </tr>

                <tr *ngIf="!loading && error">
                    <td class="dh-state" [attr.colspan]="columnCount">
                        <div class="dh-error" data-testid="documents-error">
                            {{ 'abcp_documents_error' | i18next }}
                        </div>
                    </td>
                </tr>

                <tr *ngIf="!loading && !error && isEmpty">
                    <td class="dh-state" [attr.colspan]="columnCount">
                        <div class="dh-empty" data-testid="documents-empty">{{ 'abcp_documents_empty' | i18next }}</div>
                    </td>
                </tr>

                <!-- REV-22366 — a file being uploaded, until the list holds its document. -->
                <tr
                    class="dh-row dh-row--uploading"
                    data-testid="documents-uploading-row"
                    aria-busy="true"
                    *ngFor="let filename of uploadRows"
                >
                    <td class="dh-cell dh-cell--name">
                        <div class="dh-name">
                            <lucide-icon
                                class="dh-name__icon"
                                name="file-text"
                                [size]="14"
                                aria-hidden="true"
                            ></lucide-icon>
                            <span class="dh-name__text">{{ filename }}</span>
                        </div>
                    </td>
                    <td class="dh-cell" [attr.colspan]="columnCount - 1">
                        <span class="dh-uploading">
                            <lucide-icon name="loader-circle" [size]="14" aria-hidden="true"></lucide-icon>
                            {{ 'abcp_documents_uploading' | i18next }}
                        </span>
                    </td>
                </tr>

                <tr
                    class="dh-row"
                    data-testid="documents-row"
                    *ngFor="let row of documents; trackBy: trackById"
                    [attr.data-assignment]="row.assignment"
                    [attr.data-filing]="row.filing"
                    [attr.data-source]="row.source"
                    [attr.data-not-relevant]="row.notRelevant"
                    [attr.data-proposal-pending]="proposalPendingIds.includes(row.id) ? 'true' : null"
                    [attr.data-ai-state]="aiState(row)"
                    [class.is-busy]="isRowPending(row)"
                    [attr.data-busy]="isRowPending(row) ? 'true' : null"
                    [attr.aria-busy]="isRowPending(row) ? 'true' : null"
                >
                    <!-- DOKUMENTENNAME — führt auf die Einzeldokument-Seite (REV-21368). -->
                    <td class="dh-cell dh-cell--name">
                        <div
                            class="dh-name"
                            data-testid="documents-name"
                            role="button"
                            tabindex="0"
                            (click)="onRowClick(row.id)"
                            (keydown.enter)="onRowClick(row.id)"
                            (keydown.space)="onRowClick(row.id); $event.preventDefault()"
                        >
                            <lucide-icon class="dh-name__icon" name="file-text" [size]="14" aria-hidden="true"></lucide-icon>
                            <span
                                class="dh-name__text"
                                [hamTooltip]="row.filename"
                                tooltipClass="abcp"
                                [tooltipOnlyWhenTruncated]="true"
                                >{{ row.filename }}</span
                            >
                            <!--
                                REV-21964 · C1–C4 — where the upstream file stands.

                                ⚠️ A MARKER BESIDE THE NAME. Never a reason to hide the row, and never
                                a change to the account column: a file deleted in SharePoint leaves
                                the document, its account and its reconciliation exactly as they were
                                (C1). Saying so is the whole job.

                                Loud or quiet is decided by the container (C2) and arrives as
                                `upstreamNoticeIsUrgent` — the view picks a style, it never reads the
                                prose to work out how alarmed to be.
                            -->
                            <span
                                class="dh-upstream"
                                [class.dh-upstream--urgent]="row.upstreamNoticeIsUrgent"
                                data-testid="documents-upstream-marker"
                                *ngIf="row.upstreamState && row.upstreamState !== 'present'"
                                [hamTooltip]="upstreamStateLabelKey(row.upstreamState) | i18next"
                                tooltipClass="abcp"
                            >
                                <lucide-icon
                                    [name]="row.upstreamState === 'replaced' ? 'history' : 'unlink'"
                                    [size]="12"
                                    aria-hidden="true"
                                ></lucide-icon>
                            </span>
                            <!-- ID2 — other upstream names for the same bytes. One document, not two rows. -->
                            <span
                                class="dh-alias"
                                data-testid="documents-alias-marker"
                                *ngIf="row.aliasHint"
                                [hamTooltip]="row.aliasHint"
                                tooltipClass="abcp"
                            >
                                <lucide-icon name="copy" [size]="12" aria-hidden="true"></lucide-icon>
                            </span>
                            <!-- The balance is dated other than the closing's balance-sheet date: filed, but no proof here. -->
                            <span
                                class="dh-date-warning"
                                data-testid="documents-date-warning"
                                role="img"
                                *ngIf="row.dateWarning as warning"
                                [attr.aria-label]="'abcp_documents_date_warning' | i18next: dateWarningParams(warning)"
                                [hamTooltip]="'abcp_documents_date_warning' | i18next: dateWarningParams(warning)"
                                tooltipClass="abcp"
                            >
                                <lucide-icon name="triangle-alert" [size]="12" aria-hidden="true"></lucide-icon>
                            </span>
                            <!-- Dauerakte: wandert automatisch in den Folgeabschluss (§5). -->
                            <span
                                class="dh-validity"
                                data-testid="documents-validity-marker"
                                *ngIf="row.validity === 'longTerm'"
                                [hamTooltip]="row.validityHint || ('abcp_documents_validity_long_term' | i18next)"
                                tooltipClass="abcp"
                            >
                                <lucide-icon name="clock" [size]="12" aria-hidden="true"></lucide-icon>
                            </span>
                        </div>
                    </td>

                    <!-- KONTO — hier NUR gerendert und emittiert; geschrieben wird in REV-21602. -->
                    <td class="dh-cell dh-cell--account">
                        <div class="dh-account">
                            <!-- bestätigt: Häkchen für den Menschen, Bot für den Agenten -->
                            <span
                                class="dh-chip dh-chip--account"
                                data-testid="documents-account-chip"
                                *ngIf="isConfirmed(row)"
                                [attr.data-origin]="row.assignedBy || null"
                                [hamTooltip]="row.account?.label"
                                tooltipClass="abcp"
                                [tooltipOnlyWhenTruncated]="true"
                            >
                                <!-- KEIN Icon bei Altbestand (`assignedBy` fehlt): eine unbekannte
                                     Herkunft darf weder Haekchen (Mensch) noch Bot (Agent) behaupten
                                     — beides waere eine Aussage ueber etwas, das niemand weiss. -->
                                <lucide-icon
                                    *ngIf="row.assignedBy"
                                    [name]="row.assignedBy === 'agent' ? 'bot-message-square' : 'check'"
                                    [size]="11"
                                    aria-hidden="true"
                                ></lucide-icon>
                                {{ row.account?.label }}
                            </span>

                            <!-- Projekt-Akte: abgelegt und relevant, aber zu KEINEM Konto (§9a).
                                 Steht in der Pille `Zugeordnet`, nicht in einer eigenen. -->
                            <span
                                class="dh-chip dh-chip--project"
                                data-testid="documents-project-file-chip"
                                *ngIf="isProjectFile(row)"
                                [hamTooltip]="'abcp_documents_project_file' | i18next"
                                tooltipClass="abcp"
                                [tooltipOnlyWhenTruncated]="true"
                            >
                                <lucide-icon name="folder" [size]="11" aria-hidden="true"></lucide-icon>
                                {{ 'abcp_documents_project_file' | i18next }}
                            </span>

                            <!-- Vorschlag: ein Konto = Chip mit ✓; zwei Konten = je eine klickbare Pille; ✗ = keiner. -->
                            <ng-container *ngIf="isProposal(row)">
                                <ng-container *ngIf="proposalCandidates(row) as candidates">
                                    <ng-container *ngIf="candidates.length === 1; else twoProposals">
                                        <span
                                            class="dh-chip dh-chip--proposal"
                                            data-testid="documents-proposal-chip"
                                            [attr.data-certainty]="row.proposalCertainty || null"
                                            [hamTooltip]="
                                                candidates[0].hints.length
                                                    ? (candidates[0].hints[0] | i18next) +
                                                      (candidates[0].hints[1] ? ' · ' + (candidates[0].hints[1] | i18next) : '')
                                                    : candidates[0].label
                                            "
                                            tooltipClass="abcp"
                                            [tooltipOnlyWhenTruncated]="!candidates[0].hints.length"
                                        >
                                            <lucide-icon [name]="certaintyIcon(row, false)" [size]="11" aria-hidden="true"></lucide-icon>
                                            {{ candidates[0].label }}
                                        </span>
                                        <button
                                            class="dh-icon-btn dh-icon-btn--accept"
                                            type="button"
                                            data-testid="documents-proposal-accept"
                                            [attr.aria-label]="'abcp_documents_proposal_accept' | i18next"
                                            [hamTooltip]="'abcp_documents_proposal_accept' | i18next"
                                            tooltipClass="abcp"
                                            [disabled]="proposalPendingIds.includes(row.id)"
                                            (click)="onProposalAccept(row.id, candidates[0].id)"
                                        >
                                            <lucide-icon name="check" [size]="12" aria-hidden="true"></lucide-icon>
                                        </button>
                                    </ng-container>
                                    <ng-template #twoProposals>
                                        <div class="dh-proposals">
                                            <button
                                                class="dh-chip dh-chip--proposal"
                                                type="button"
                                                [attr.data-testid]="candidate.alternative ? 'documents-proposal-accept-alternative' : 'documents-proposal-accept'"
                                                *ngFor="let candidate of candidates; trackBy: trackById"
                                                [attr.data-account-id]="candidate.id"
                                                [attr.data-alternative]="candidate.alternative || null"
                                                [attr.data-certainty]="candidate.alternative ? null : row.proposalCertainty || null"
                                                [attr.aria-label]="('abcp_documents_proposal_accept' | i18next) + ': ' + candidate.label"
                                                [hamTooltip]="
                                                    candidate.hints.length
                                                        ? (candidate.hints[0] | i18next) +
                                                          (candidate.hints[1] ? ' · ' + (candidate.hints[1] | i18next) : '')
                                                        : candidate.label
                                                "
                                                tooltipClass="abcp"
                                                [tooltipOnlyWhenTruncated]="!candidate.hints.length"
                                                [disabled]="proposalPendingIds.includes(row.id)"
                                                (click)="onProposalAccept(row.id, candidate.id)"
                                            >
                                                <lucide-icon [name]="certaintyIcon(row, candidate.alternative)" [size]="11" aria-hidden="true"></lucide-icon>
                                                {{ candidate.label }}
                                            </button>
                                        </div>
                                    </ng-template>
                                </ng-container>
                                <button
                                    class="dh-icon-btn dh-icon-btn--reject"
                                    type="button"
                                    data-testid="documents-proposal-reject"
                                    [attr.aria-label]="'abcp_documents_proposal_reject' | i18next"
                                    [hamTooltip]="'abcp_documents_proposal_reject' | i18next"
                                    tooltipClass="abcp"
                                    [disabled]="proposalPendingIds.includes(row.id)"
                                    (click)="onProposalReject(row, $any($event.currentTarget).closest('.dh-account')); $event.stopPropagation()"
                                >
                                    <lucide-icon name="x" [size]="12" aria-hidden="true"></lucide-icon>
                                </button>
                                <!-- Ein SCHREIBFEHLER ist NICHT der Ladefehler (F5): eigenes Element
                                     an der Zeile, `documents-error` bleibt fuer den Ladefehler reserviert. -->
                                <span
                                    class="dh-proposal-error"
                                    data-testid="documents-proposal-error"
                                    *ngIf="proposalFailedIds.includes(row.id)"
                                    [hamTooltip]="'abcp_documents_proposal_error' | i18next"
                                    tooltipClass="abcp"
                                >
                                    <lucide-icon name="alert-circle" [size]="12" aria-hidden="true"></lucide-icon>
                                </span>
                            </ng-container>

                            <!-- offen: was die KI dazu sagt (REV-22302), und der zweistufige Wähler
                                 des Prototyps (`inline-konto-dd`). Kein ham-/aum-Dropdown kommt auf
                                 diese Form — angehefteter Projekt-Akte-Eintrag, Klassen-Spalte, und
                                 die Gültigkeitsfrage als zweiter Schritt derselben Handlung. -->
                            <div class="dh-open" *ngIf="isUnfiled(row)">
                                <!-- Die KI ARBEITET: Punkt, Titel und der echte Schritt aus der Pipeline. -->
                                <div
                                    class="ai-working"
                                    data-testid="documents-ai-working"
                                    role="status"
                                    *ngIf="aiState(row) === 'working'"
                                    [attr.data-step]="row.processingStep || null"
                                >
                                    <span class="ai-working__dot" aria-hidden="true"></span>
                                    <span class="ai-working__text">
                                        <span class="ai-working__title">{{ aiWorkingKeys.title | i18next }}</span>
                                        <span class="ai-working__step" data-testid="documents-ai-step" *ngIf="processingStepKey(row) as stepKey">{{
                                            stepKey | i18next
                                        }}</span>
                                    </span>
                                </div>

                                <!-- KEIN VORSCHLAG / NICHT LESBAR: der Befund als Text, der Grund im
                                     Info-Tooltip — und daneben der Waehler, kompakt. -->
                                <span
                                    class="dh-ai-note"
                                    data-testid="documents-ai-note"
                                    *ngIf="aiNoteLabelKey(row) as noteKey"
                                    [attr.data-state]="aiState(row)"
                                >
                                    {{ noteKey | i18next }}
                                    <i
                                        class="dh-ai-note__glyph"
                                        data-testid="documents-ai-note-info"
                                        role="img"
                                        [attr.aria-label]="aiNoteReasonKey(row) | i18next"
                                        [hamTooltip]="aiNoteReasonKey(row) | i18next"
                                        tooltipClass="abcp"
                                    ></i>
                                </span>

                                <div
                                    class="dh-picker"
                                    *ngIf="showsPicker(row)"
                                    [class.dh-picker--compact]="isPickerCompact(row)"
                                >
                                    <button
                                        class="dh-picker__trigger"
                                        type="button"
                                        data-testid="documents-account-select"
                                        [attr.aria-expanded]="isPickerOpen(row)"
                                        (click)="onPickerToggle(row, $any($event.currentTarget)); $event.stopPropagation()"
                                    >
                                        {{
                                            (isPickerCompact(row) ? aiStateLabelKeys.choose : 'abcp_documents_account_select')
                                                | i18next
                                        }}
                                        <i class="dh-picker__chevron" aria-hidden="true"></i>
                                    </button>

                                    <!-- In der globalen Flyout-Ebene (`hamFlyout`), nicht in der Zeile: die
                                         Tabelle scrollt in ihrem Rahmen und schnitte das Panel ab.
                                         stopPropagation: der document-Listener schliesst sonst das
                                         Panel beim ersten Klick darin. -->
                                    <div
                                        #pickerPanel
                                        class="dh-picker__panel"
                                        data-testid="documents-account-picker"
                                        hamFlyout
                                        [position]="pickerPosition"
                                        *ngIf="isPickerOpen(row)"
                                        (closeFlyout)="closePicker()"
                                        (click)="$event.stopPropagation()"
                                    >
                                        <!-- SCHRITT 1 — Konto oder Projekt-Akte -->
                                        <ng-container *ngIf="pickerStep === 'account'">
                                            <div class="dh-picker__search">
                                                <lucide-icon name="search" [size]="13" aria-hidden="true"></lucide-icon>
                                                <input
                                                    type="text"
                                                    data-testid="documents-account-search"
                                                    [value]="pickerSearch"
                                                    [attr.placeholder]="'abcp_documents_picker_search_placeholder' | i18next"
                                                    [attr.aria-label]="'abcp_documents_picker_search_placeholder' | i18next"
                                                    (input)="onPickerSearch($any($event.target).value)"
                                                />
                                            </div>

                                            <div class="dh-picker__list" data-testid="documents-account-list">
                                                <!-- Angeheftet und nur ohne Suchbegriff: die Projekt-Akte
                                                     ist ein Ablageziel, kein Suchergebnis (§9a). -->
                                                <button
                                                    class="dh-picker__row dh-picker__row--project"
                                                    type="button"
                                                    data-testid="documents-picker-project-file"
                                                    *ngIf="isProjectFileOptionVisible"
                                                    (click)="onProjectFilePick()"
                                                >
                                                    <lucide-icon
                                                        class="dh-picker__row-icon"
                                                        name="clipboard-list"
                                                        [size]="14"
                                                        aria-hidden="true"
                                                    ></lucide-icon>
                                                    <span class="dh-picker__row-main">
                                                        {{ 'abcp_documents_picker_project_file' | i18next }}
                                                        <span class="dh-picker__row-sub">{{
                                                            'abcp_documents_picker_project_file_hint' | i18next
                                                        }}</span>
                                                    </span>
                                                </button>

                                                <button
                                                    class="dh-picker__row"
                                                    type="button"
                                                    data-testid="documents-picker-account"
                                                    *ngFor="let account of filteredAccounts; trackBy: trackById"
                                                    [attr.data-account-id]="account.id"
                                                    (click)="onAccountPick(account.id)"
                                                >
                                                    <span class="dh-picker__number">{{ account.number }}</span>
                                                    <span class="dh-picker__name">{{ account.name }}</span>
                                                    <span
                                                        class="dh-picker__position"
                                                        [hamTooltip]="account.klasse"
                                                        tooltipClass="abcp"
                                                        [tooltipOnlyWhenTruncated]="true"
                                                        >{{ account.klasse }}</span
                                                    >
                                                </button>

                                                <div
                                                    class="dh-picker__empty"
                                                    data-testid="documents-picker-empty"
                                                    *ngIf="!filteredAccounts.length"
                                                >
                                                    {{ 'abcp_documents_picker_empty' | i18next }}
                                                </div>
                                            </div>
                                        </ng-container>

                                        <!-- SCHRITT 2 — Gültigkeit. Erst hier verlässt die Zuordnung die
                                             View, damit Ziel und Gültigkeit EIN Ereignis sind. -->
                                        <ng-container *ngIf="pickerStep === 'validity'">
                                            <div class="dh-picker__head">
                                                <div class="dh-picker__head-label">
                                                    {{ 'abcp_documents_picker_assignment' | i18next }}
                                                </div>
                                                <div class="dh-picker__head-target" data-testid="documents-picker-target">
                                                    <ng-container *ngIf="isProjectFileTarget"
                                                        ><lucide-icon
                                                            name="clipboard-list"
                                                            [size]="14"
                                                            aria-hidden="true"
                                                        ></lucide-icon>
                                                        {{ 'abcp_documents_project_file' | i18next }}</ng-container
                                                    >
                                                    <ng-container *ngIf="!isProjectFileTarget">{{ pickerTargetLabel }}</ng-container>
                                                </div>
                                            </div>

                                            <div class="dh-picker__validity">
                                                <button
                                                    class="dh-picker__row dh-picker__row--stacked"
                                                    type="button"
                                                    data-testid="documents-picker-validity-closing"
                                                    (click)="onValidityPick('closing')"
                                                >
                                                    <span class="dh-picker__row-title">{{
                                                        'abcp_documents_validity_closing' | i18next
                                                    }}</span>
                                                    <span class="dh-picker__row-sub">{{
                                                        'abcp_documents_validity_closing_hint' | i18next
                                                    }}</span>
                                                </button>
                                                <button
                                                    class="dh-picker__row dh-picker__row--stacked"
                                                    type="button"
                                                    data-testid="documents-picker-validity-long-term"
                                                    (click)="onValidityPick('longTerm')"
                                                >
                                                    <span class="dh-picker__row-title"
                                                        ><lucide-icon
                                                            name="clock"
                                                            [size]="14"
                                                            aria-hidden="true"
                                                        ></lucide-icon>
                                                        {{ 'abcp_documents_validity_long_term_option' | i18next }}</span
                                                    >
                                                    <span class="dh-picker__row-sub">{{
                                                        'abcp_documents_validity_long_term_hint' | i18next
                                                    }}</span>
                                                </button>
                                            </div>

                                            <div class="dh-picker__foot">
                                                <ham-button
                                                    data-testid="documents-picker-back"
                                                    variant="transparent"
                                                    size="toolbar"
                                                    icon="assets/hamIcons/arrow-left-outline.svg"
                                                    (buttonClick)="onPickerBack()"
                                                >
                                                    {{ 'abcp_documents_picker_back' | i18next }}
                                                </ham-button>
                                            </div>
                                        </ng-container>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </td>

                    <!-- BELEGTYP (REV-22302) — die erkannte Belegart als neutrale Beschriftung. NIE die
                         Prozentzahl: die Selbstauskunft des Modells las sich als Sicherheit ueber die
                         Kontowahl und stand auf fast jeder Zeile bei 100 %. `—` ohne erkannte Art (§8). -->
                    <td class="dh-cell dh-cell--confidence">
                        <span
                            class="dh-conf"
                            data-testid="documents-confidence"
                            data-variant="gray"
                            *ngIf="kindLabelKey(row) as kindKey; else noKind"
                            [attr.data-kind]="row.kind"
                            >{{ kindKey | i18next }}</span
                        >
                        <ng-template #noKind
                            ><span class="dh-conf dh-conf--empty" data-testid="documents-confidence">—</span></ng-template
                        >
                    </td>

                    <td class="dh-cell dh-cell--date" data-testid="documents-date">
                        {{ row.addedAt | date: dateFormat }}
                    </td>

                    <td class="dh-cell dh-cell--source" data-testid="documents-source">
                        {{ sourceLabelKey(row.source) | i18next }}
                    </td>

                    <!-- Bewusst KEIN `ham-badge-button`: der Prototyp-Badge (`.bdg`) ist ein
                         Rechteck mit Radius 6, die DS-Pille ist rund. Prototyp-Treue gewinnt. -->
                    <td class="dh-cell dh-cell--status">
                        <span
                            class="dh-badge"
                            data-testid="documents-status"
                            [attr.data-variant]="statusVariant(row.assignment)"
                        >
                            {{ statusLabelKey(row.assignment) | i18next }}
                        </span>
                    </td>

                    <!-- DIE EINZIGE ZEILENAKTION. Kein Papierkorb, in keinem Filter (§4): dieselbe
                         Stelle kehrt die Markierung auch wieder um, damit die Rücknahme dort
                         erreichbar ist, wo man die Folge sieht. -->
                    <td class="dh-cell dh-cell--actions">
                        <!-- hamIcons has an eye but no crossed-out eye, hence the Lucide `eye-off`. -->
                        <span class="dh-busy" data-testid="documents-row-busy" *ngIf="isRowPending(row)">
                            <lucide-icon name="loader-circle" [size]="14" aria-hidden="true"></lucide-icon>
                        </span>
                        <ham-button
                            *ngIf="!isRowPending(row)"
                            class="dh-action"
                            data-testid="documents-not-relevant-toggle"
                            variant="transparent"
                            size="toolbar"
                            [icon]="row.notRelevant ? 'assets/hamIcons/eye-outline.svg' : null"
                            [lucideIcon]="row.notRelevant ? null : 'eye-off'"
                            [hideText]="true"
                            [attr.data-state]="row.notRelevant ? 'restore' : 'mark'"
                            [hamTooltip]="
                                (row.notRelevant ? 'abcp_documents_not_relevant_restore' : 'abcp_documents_not_relevant_mark')
                                    | i18next
                            "
                            tooltipClass="abcp"
                            (buttonClick)="onNotRelevantToggle(row)"
                        ></ham-button>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</div>
